import React, { memo } from 'react';
import { Flex } from '@atlaskit/primitives/compiled';
import { MenuSection, MenuSectionHeading, MenuList } from '@atlaskit/navigation-system';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { PaidFeatureAwarenessRenderer } from '@atlassian/jira-paid-feature-awareness-renderer/src/ui/PaidFeatureAwarenessRendererComponent.tsx';
import { PaidFeatureAwarenessGemIcon } from '@atlassian/jira-paid-feature-awareness-renderer/src/ui/icons/index.tsx';
import LinkMenuItem from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';
import messages from './messages.tsx';

const PlansSection = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();

	return (
		<MenuSection isMenuListItem={fg('platform_design_system_nav4_sidenav_a11y')}>
			<MenuSectionHeading>
				<Flex alignItems="center" justifyContent="space-between">
					{formatMessage(messages.heading)}
					<PaidFeatureAwarenessRenderer
						component={<PaidFeatureAwarenessGemIcon edition="premium" />}
						placement="navigation.plan.settings"
					/>
				</Flex>
			</MenuSectionHeading>
			<MenuList>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'permissions' }}
					isSelected={currentRoute.name === 'arj-settings-permissions'}
					href="/jira/plans/settings/permissions"
				>
					{formatMessage(messages.permissionsItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'hierarchyConfiguration' }}
					isSelected={currentRoute.name === 'global-settings-issues-hierarchy-view'}
					href="/jira/settings/issues/issue-hierarchy"
				>
					{formatMessage(messages.hierarchyItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'dependencies' }}
					isSelected={currentRoute.name === 'arj-settings-dependencies'}
					href="/jira/plans/settings/dependencies"
				>
					{formatMessage(messages.dependenciesItem)}
				</LinkMenuItem>
				{fg('jira_plans_loading_limits_configurability_stsg') && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'loadingLimits' }}
						isSelected={currentRoute.name === 'arj-settings-loading-limits'}
						href="/jira/plans/settings/loading-limits"
					>
						{formatMessage(messages.loadingLimitsItem)}
					</LinkMenuItem>
				)}
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'trash' }}
					isSelected={currentRoute.name === 'trash-plans-directory'}
					href="/jira/plans/settings/trash"
				>
					{formatMessage(messages.trashedPlansItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'archive' }}
					isSelected={currentRoute.name === 'archived-plans-directory'}
					href="/jira/plans/settings/archive"
				>
					{formatMessage(messages.archiveItem)}
				</LinkMenuItem>
				<LinkMenuItem
					analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'financialYear' }}
					isSelected={currentRoute.name === 'arj-settings-financial-year'}
					href="/jira/plans/settings/financial-year"
				>
					{formatMessage(messages.financialYearItem)}
				</LinkMenuItem>
			</MenuList>
		</MenuSection>
	);
};

export default memo(PlansSection);
